import { useCallback, useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';
import { canvasRoots } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';
import { useCanvasReadOnly } from '../wiring/contexts/canvas-read-only-context';
import styles from './edge-label.module.css';

export interface EdgeLabelProps {
  doc: Y.Doc;
  clientId: number;
  edgeId: string;
  /** Current label text from edge.data.label. */
  value: string;
  /** External request to enter edit mode (e.g., from toolbar T button).
   *  Bumping this number focuses the input. */
  editToken: number;
}

/**
 * Inline edge label — renders as a white pill at the edge midpoint when
 * non-empty. Double-click on the pill (or external editToken bump) opens
 * an editable input. Enter / blur commits; Esc cancels. Empty value on
 * commit removes the chip entirely.
 */
export function EdgeLabel({ doc, clientId, edgeId, value, editToken }: EdgeLabelProps) {
  const binding = useCanvasBinding();
  const readOnly = useCanvasReadOnly();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  // A read-only canvas is never in edit mode, regardless of edit-entry path
  // (toolbar T-button token bump OR pill double-click).
  const isEditing = editing && !readOnly;

  // External edit-trigger from toolbar T-button: bumping editToken opens
  // the editor. Initial mount (token === 0) does NOT auto-enter edit.
  const lastTokenRef = useRef(editToken);
  useEffect(() => {
    if (editToken !== lastTokenRef.current) {
      lastTokenRef.current = editToken;
      setDraft(value);
      setEditing(true);
    }
  }, [editToken, value]);

  // Sync draft when value changes externally and we're not editing.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  // Auto-focus + select-all when editing begins.
  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [isEditing]);

  const commit = useCallback(() => {
    setEditing(false);
    if (draft === value) return;
    doc.transact(() => {
      const edge = canvasRoots(doc, binding).edges.get(edgeId);
      if (!edge) return;
      const data = edge.get('data');
      if (!(data instanceof Y.Map)) return;
      data.set('label', draft);
      edge.set('updatedAt', Date.now());
    }, clientId);
  }, [doc, clientId, edgeId, draft, value, binding]);

  const cancel = useCallback(() => {
    setDraft(value);
    setEditing(false);
  }, [value]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        className={styles.input}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        // Stop pointer events from reaching the edge underneath while
        // editing — clicks/drags on the input shouldn't deselect the edge.
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label="Edge label"
      />
    );
  }

  if (value.length === 0) return null;

  return (
    <div
      className={styles.pill}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (readOnly) return;
        setDraft(value);
        setEditing(true);
      }}
      // Single click shouldn't deselect the edge — let it bubble for now,
      // but prevent the edge's own dblclick handler from firing twice.
    >
      {value}
    </div>
  );
}
